import {
  ATTRIBUTION_MODEL,
  ChartType,
  DashboardView,
  DashboardViewConfiguration,
  DashboardWidget,
  GRANULARITY,
  MEASURE,
} from '@adtrackify/at-tracking-event-types';

import { getDateRangeFromPreset } from '@/lib/helpers/dates/date-ranges';
import { resolveMeasureAlias } from '@/lib/helpers/measures/measure-definitions';
import { getInvalidDashboardWidgetLayoutIds, normalizeDashboardWidgetLayouts } from '@/features/dashboard/helpers/dashboard-layout-controller';
import { takePendingDashboardViewName } from './pending-view-selection';
import { createSummaryCatalogSections, flattenSectionsToWidgets } from './section-catalog-helpers';
import {
  getDashboardWidgetOrdinal,
  sortWidgetsByLegacyVisualOrder,
} from './widget-ordering';

export function viewHasDeprecatedMeasures(view: DashboardView): boolean {
  const widgets = view.configuration?.widgets;
  if (!widgets) {
    return false;
  }
  return widgets.some((widget) =>
    widget.dataConfig.measures?.some(
      (measure) => resolveMeasureAlias(measure) !== measure
    )
  );
}

export function viewHasMissingOrdinals(view: DashboardView): boolean {
  const widgets = view.configuration?.widgets ?? [];
  if (widgets.length === 0) return false;
  return widgets.some(widget => getDashboardWidgetOrdinal(widget) === undefined);
}

export function backfillWidgetOrdinals(widgets: DashboardWidget[]): DashboardWidget[] {
  if (widgets.length === 0) return widgets;

  const orderedWidgets = sortWidgetsByLegacyVisualOrder(widgets);
  const ordinalById = new Map<string, number>();
  orderedWidgets.forEach((widget, index) => {
    ordinalById.set(widget.id, index);
  });

  return widgets.map(widget => {
    const existingOrdinal = getDashboardWidgetOrdinal(widget);
    if (existingOrdinal !== undefined) return widget;

    return {
      ...widget,
      layout: {
        ...widget.layout,
        ordinal: ordinalById.get(widget.id),
      },
    };
  });
}

export function normalizeViewOrdinals(view: DashboardView): DashboardView {
  if (!view.configuration) {
    return view;
  }
  return {
    ...view,
    configuration: {
      ...view.configuration,
      widgets: backfillWidgetOrdinals(view.configuration.widgets ?? []),
    },
  };
}

export function getInvalidWidgetLayoutIds(view: DashboardView): string[] {
  return getInvalidDashboardWidgetLayoutIds(view.configuration?.widgets ?? []);
}

export function resolveViewMeasures(view: DashboardView): DashboardView {
  if (!view.configuration) {
    return view;
  }
  return {
    ...view,
    configuration: {
      ...view.configuration,
      widgets: (view.configuration.widgets ?? []).map((widget) => ({
        ...widget,
        chartType: widget.chartType as ChartType,
        dataConfig: {
          ...widget.dataConfig,
          measures: widget.dataConfig.measures?.map((measure) =>
            resolveMeasureAlias(measure)
          ) as MEASURE[],
        },
      })),
    },
  };
}

export function normalizeViewWidgetLayouts(view: DashboardView): DashboardView {
  if (!view.configuration) {
    return view;
  }
  return {
    ...view,
    configuration: {
      ...view.configuration,
      widgets: normalizeDashboardWidgetLayouts(view.configuration.widgets ?? []),
    },
  };
}

import { UPSTACK_TEMPLATE_NAME, OVERVIEW_VIEW_NAME, SUMMARY_VIEW_NAME } from '../constants/view-name-constants';
import type { DefaultViewTemplateInput } from '../types/overview-types';

const DEFAULT_VIEW_NAME_FALLBACKS = [
  SUMMARY_VIEW_NAME,
  OVERVIEW_VIEW_NAME,
  UPSTACK_TEMPLATE_NAME,
];

export function isDefaultView(view: DashboardView): boolean {
  return DEFAULT_VIEW_NAME_FALLBACKS.includes(view.name);
}

export function findDefaultView(views: DashboardView[]): DashboardView | undefined {
  for (const name of DEFAULT_VIEW_NAME_FALLBACKS) {
    const match = views.find(view => view.name === name);
    if (match) {
      return match;
    }
  }
  return undefined;
}

export function hasSummaryView(views: DashboardView[]): boolean {
  return views.some(view => view.name === SUMMARY_VIEW_NAME);
}

export function needsSummaryViewBackfill(views: DashboardView[]): boolean {
  return views.length > 0 && !hasSummaryView(views);
}

export function findUpstackTemplate(views: DashboardView[]): DashboardView | undefined {
  return findDefaultView(views);
}

export function sortViewsWithTemplateFirst(views: DashboardView[]): DashboardView[] {
  if (!views || views.length === 0) return views;

  const defaultView = findDefaultView(views);
  if (!defaultView) {
    return [...views].sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  }

  const otherViews = views
    .filter(view => view.id !== defaultView.id)
    .sort((a, b) => (a.name || '').localeCompare(b.name || ''));

  return [defaultView, ...otherViews];
}

export function resolveDashboardViewLabel(view: { id: string; name?: string }): string {
  const trimmedName = view.name?.trim();
  if (!trimmedName || trimmedName === view.id) {
    return 'Unnamed View';
  }
  return trimmedName;
}

export function buildDashboardViewOptions(args: {
  views?: Array<{ id: string; name?: string }> | null;
  currentView?: { id: string; name?: string } | null;
}): Array<{ value: string; label: string }> {
  const optionsById = new Map<string, { value: string; label: string }>();
  for (const view of args.views ?? []) {
    optionsById.set(view.id, {
      value: view.id,
      label: resolveDashboardViewLabel(view),
    });
  }
  if (args.currentView && !optionsById.has(args.currentView.id)) {
    optionsById.set(args.currentView.id, {
      value: args.currentView.id,
      label: resolveDashboardViewLabel(args.currentView),
    });
  }
  return [...optionsById.values()];
}

export function resolvePickerViewId(
  selectedViewId: string | null,
  viewOptions: Array<{ value: string }>,
  currentViewId?: string | null
): string | null {
  if (selectedViewId && viewOptions.some(option => option.value === selectedViewId)) {
    return selectedViewId;
  }
  if (currentViewId && viewOptions.some(option => option.value === currentViewId)) {
    return currentViewId;
  }
  return null;
}

export function selectViewWhenMissing(
  views: DashboardView[],
  selectedViewId: string | null,
  urlViewId: string | null,
  storedDefaultViewId: string | null,
  setSelectedViewId: (viewId: string | null) => void
): void {
  const pendingViewId = takePendingDashboardViewName(views);
  if (pendingViewId) {
    if (pendingViewId !== selectedViewId) {
      setSelectedViewId(pendingViewId);
    }
    return;
  }
  if (selectedViewId && views.some(view => view.id === selectedViewId)) {
    return;
  }
  const bestViewId = selectBestView(views, urlViewId, storedDefaultViewId);
  if (bestViewId) {
    setSelectedViewId(bestViewId);
  }
}

export function selectBestView(
  views: DashboardView[],
  urlViewId: string | null,
  storedDefaultViewId?: string | null,
  selectedViewId?: string | null
): string | null {
  if (!views.length) return null;

  if (urlViewId && views.some(view => view.id === urlViewId)) {
    return urlViewId;
  }

  if (storedDefaultViewId && views.some(view => view.id === storedDefaultViewId)) {
    return storedDefaultViewId;
  }

  if (selectedViewId && views.some(view => view.id === selectedViewId)) {
    return selectedViewId;
  }

  const defaultView = findDefaultView(views);
  if (defaultView) {
    return defaultView.id;
  }

  return views[0].id;
}

export function isConfiguredDashboardView(
  view: DashboardView | null | undefined
): view is DashboardView {
  return view?.configuration != null;
}

export function resolveMatchingDashboardView(args: {
  selectedView?: DashboardView;
  bootstrapCurrentView: DashboardView | null;
  views?: DashboardView[];
  effectiveViewId: string | null;
}): DashboardView | null {
  const { selectedView, bootstrapCurrentView, views, effectiveViewId } = args;
  if (!effectiveViewId) {
    return isConfiguredDashboardView(bootstrapCurrentView) ? bootstrapCurrentView : null;
  }
  if (selectedView?.id === effectiveViewId && isConfiguredDashboardView(selectedView)) {
    return selectedView;
  }
  if (
    bootstrapCurrentView?.id === effectiveViewId &&
    isConfiguredDashboardView(bootstrapCurrentView)
  ) {
    return bootstrapCurrentView;
  }
  const listedView = views?.find(view => view.id === effectiveViewId);
  if (isConfiguredDashboardView(listedView)) {
    return listedView;
  }
  return null;
}

export function createDefaultViewConfig(
  defaultView?: DefaultViewTemplateInput
): DashboardViewConfiguration {
  const defaultDateRange = getDateRangeFromPreset('last30');
  const sections = createSummaryCatalogSections();
  const widgets = flattenSectionsToWidgets(sections);

  return {
    attributionWindow: defaultView?.configuration.attributionWindow ?? 30,
    attributionModel: (defaultView?.configuration.attributionModel ??
      ATTRIBUTION_MODEL.FIRST_CLICK) as ATTRIBUTION_MODEL,
    filters: {
      ...defaultView?.configuration.filters,
      preset: 'last30',
      enableCompareDate: undefined,
      compareDateRange: undefined,
    },
    granularity: (defaultView?.configuration.granularity ?? GRANULARITY.DAY) as GRANULARITY,
    dateRange: {
      start: defaultDateRange.start,
      end: defaultDateRange.end,
    },
    grid: {
      maxWidth: defaultView?.configuration.grid.maxWidth ?? 1200,
    },
    widgets,
    sections,
  };
}
